import type { ComponentConfig } from '@measured/puck';
import { Container } from '../layout/Container';
import { Badge } from '../ui/badge';

export interface CaseStudyHighlightProps {
  client: string;
  sector: string;
  challenge: string;
  results: string[];
  slug?: string;
}

export function CaseStudyHighlight({
  client,
  sector,
  challenge,
  results,
  slug,
}: CaseStudyHighlightProps) {
  return (
    <section className="bg-stone-50 py-16">
      <Container>
        <div className="grid gap-8 rounded-2xl border border-stone-200 bg-white p-8 shadow-soft lg:grid-cols-[1fr,1.1fr]">
          <div>
            <Badge variant="default">{sector}</Badge>
            <h2 className="mt-3 text-3xl font-bold tracking-tightish">{client}</h2>
            <p className="mt-4 text-stone-700">{challenge}</p>
            {slug ? (
              <a
                href={`/cas-clients/${slug}`}
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-700 hover:underline"
              >
                Lire le dossier complet →
              </a>
            ) : null}
          </div>
          <ul className="space-y-3">
            {results.map((r) => (
              <li key={r} className="flex items-start gap-3 rounded-lg bg-stone-50 p-3">
                <span className="mt-1 inline-block h-2 w-2 rounded-full bg-brand-500" aria-hidden />
                <span className="text-stone-800">{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </section>
  );
}

export const CaseStudyHighlightConfig: ComponentConfig<CaseStudyHighlightProps> = {
  fields: {
    client: { type: 'text' },
    sector: { type: 'text' },
    challenge: { type: 'textarea' },
    slug: { type: 'text' },
    results: {
      type: 'array',
      arrayFields: { _: { type: 'text' } as never },
      defaultItemProps: '' as never,
    },
  },
  defaultProps: {
    client: 'Hôtel 4★ Lyon',
    sector: 'Tertiaire',
    challenge: 'Conformité Décret Tertiaire et valorisation CEE des travaux d’isolation toiture.',
    results: [
      '127 000 € de primes CEE valorisées',
      '-32 % de consommation finale',
      'Conformité OPERAT 2024 documentée',
    ],
    slug: '',
  },
  render: CaseStudyHighlight,
};
